import * as React from 'react';
import { clientId, IAppInfo, IPortalMe, portalCall } from './auth';
import { EmptyArt, GoogleMark, Icon, MicrosoftMark } from './brand';
import { Link, Shell } from './Shell';

// Admin area.
//   /admin/team     - People & roles (People.tsx): invitations, roles, permissions (platform owner: any client)
//   /admin/clients  - platform owner: client organizations, providers, apps, consent links

interface IClientRow { tenantId: string; name: string; active: boolean; provider: 'entra' | 'google'; domain: string; created: string; apps: string[]; owners: string[]; people: number; }

export const AdminLayout: React.FunctionComponent<{ me: IPortalMe; section: 'team' | 'clients'; children?: React.ReactNode }> = ({ me, section, children }) => (
  <Shell me={me} active="admin">
    <main className="bu-page">
      <div className="bu-admin">
        <nav className="bu-side">
          <div className="bu-side-label">Admin</div>
          <Link to="/admin/team" className={section === 'team' ? 'active' : ''}><Icon name="users" size={18} /> People &amp; roles</Link>
          {me.isOwner && <Link to="/admin/clients" className={section === 'clients' ? 'active' : ''}><Icon name="building" size={18} /> Clients</Link>}
          <div style={{ height: 14 }} />
          <Link to="/apps"><Icon name="grid" size={18} /> Back to apps</Link>
        </nav>
        <div>{children}</div>
      </div>
    </main>
  </Shell>
);

export const ProviderTag: React.FunctionComponent<{ p: 'entra' | 'google'; domain?: string }> = ({ p, domain }) => (
  <span className="bu-provider">{p === 'google' ? <GoogleMark size={16} /> : <MicrosoftMark size={15} />}{p === 'google' ? `Google · ${domain}` : 'Microsoft 365'}</span>
);

// ---------------------------------------------------------------------------
// Email list editor
// ---------------------------------------------------------------------------

const EmailList: React.FunctionComponent<{ value: string[]; onChange: (v: string[]) => void; domain?: string; placeholder?: string }> = ({ value, onChange, domain, placeholder }) => {
  const [draft, setDraft] = React.useState<string>('');
  const add = (): void => {
    const parts: string[] = draft.split(/[\s,;]+/).map((x: string): string => x.trim().toLowerCase()).filter((x: string): boolean => x !== '');
    if (parts.length === 0) { return; }
    onChange(Array.from(new Set([...value, ...parts])));
    setDraft('');
  };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input className="bu-input" value={draft} placeholder={placeholder || (domain ? `name@${domain}` : 'name@company.com')}
          onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setDraft(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} />
        <button className="bu-btn bu-btn-secondary" onClick={add} disabled={draft.trim() === ''}><Icon name="plus" size={16} /> Add</button>
      </div>
      {value.length > 0 && (
        <div className="bu-tags" style={{ marginTop: 12 }}>
          {value.map((e: string) => (
            <span key={e} className="bu-tag">{e}<button aria-label={`Remove ${e}`} onClick={(): void => onChange(value.filter((x: string): boolean => x !== e))}>&times;</button></span>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Clients (platform owner)
// ---------------------------------------------------------------------------

interface IForm { isNew: boolean; provider: 'entra' | 'google'; tenantId: string; domain: string; lookupDomain: string; name: string; active: boolean; apps: string[]; owners: string[]; currentOwners: string[]; }

const blank = (): IForm => ({ isNew: true, provider: 'entra', tenantId: '', domain: '', lookupDomain: '', name: '', active: true, apps: ['seltzer'], owners: [], currentOwners: [] });

const consentUrl = (tenantId: string): string =>
  `https://login.microsoftonline.com/${tenantId}/adminconsent?client_id=${clientId}&redirect_uri=${encodeURIComponent(window.location.origin + '/consent-complete')}`;

export const ClientsAdmin: React.FunctionComponent<{ me: IPortalMe }> = ({ me }) => {
  const [clients, setClients] = React.useState<IClientRow[] | null>(null);
  const [catalog, setCatalog] = React.useState<IAppInfo[]>([]);
  const [form, setForm] = React.useState<IForm | null>(null);
  const [msg, setMsg] = React.useState<{ ok: boolean; text: string } | null>(null);
  const [formErr, setFormErr] = React.useState<string>('');
  const [busy, setBusy] = React.useState<boolean>(false);
  const [copied, setCopied] = React.useState<string>('');

  const load = async (): Promise<void> => {
    try {
      const [c, a] = await Promise.all([portalCall<IClientRow[]>('GET', 'clients'), portalCall<IAppInfo[]>('GET', 'catalog')]);
      setClients(c); setCatalog(a);
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); }
  };
  React.useEffect((): void => { if (me.isOwner) { void load(); } }, []);

  if (!me.isOwner) { return <AdminLayout me={me} section="clients"><div className="bu-alert bu-alert-danger">Platform owner only.</div></AdminLayout>; }

  const lookup = async (): Promise<void> => {
    if (form === null) { return; }
    setBusy(true); setFormErr('');
    try {
      const r = await portalCall<{ tenantId: string }>('GET', `lookup?domain=${encodeURIComponent(form.lookupDomain)}`);
      setForm({ ...form, tenantId: r.tenantId, name: form.name || form.lookupDomain.split('.')[0].replace(/^./, (c: string): string => c.toUpperCase()) });
    } catch (e) { setFormErr((e as Error).message); } finally { setBusy(false); }
  };

  const save = async (): Promise<void> => {
    if (form === null) { return; }
    setBusy(true); setFormErr('');
    try {
      await portalCall('PUT', 'clients', { provider: form.provider, tenantId: form.tenantId, domain: form.domain, name: form.name, active: form.active, apps: form.apps, owners: form.owners });
      setMsg({ ok: true, text: form.isNew
        ? (form.provider === 'google' ? `${form.name} added. Their users can sign in with Google right away.` : `${form.name} added. Send their Microsoft 365 admin the consent link.`)
        : `${form.name} updated.` });
      setForm(null);
      await load();
    } catch (e) { setFormErr((e as Error).message); } finally { setBusy(false); }
  };

  const copy = (text: string, key: string): void => {
    void navigator.clipboard.writeText(text).then((): void => { setCopied(key); window.setTimeout((): void => setCopied(''), 1600); });
  };

  const all: IClientRow[] = clients || [];
  const canSave: boolean = form !== null && form.name.trim() !== '' && (form.provider === 'google' ? form.domain.trim() !== '' : /^[0-9a-f-]{36}$/i.test(form.tenantId));

  return (
    <AdminLayout me={me} section="clients">
      <div className="bu-page-head">
        <div>
          <p className="bu-eyebrow">Platform</p>
          <h1 className="bu-title">Clients</h1>
          <p className="bu-lede">Organizations that can sign in to BeefedUp Apps, and which apps each one gets. Every client's data is isolated from every other.</p>
        </div>
        <button className="bu-btn bu-btn-primary" onClick={(): void => { setForm(blank()); setFormErr(''); setMsg(null); }}><Icon name="plus" size={16} /> Add client</button>
      </div>

      <div className="bu-stat-row">
        <div className="bu-card bu-stat"><span>Clients</span><strong>{all.length}</strong></div>
        <div className="bu-card bu-stat"><span>Active</span><strong>{all.filter((c: IClientRow): boolean => c.active).length}</strong></div>
        <div className="bu-card bu-stat"><span>Microsoft 365</span><strong>{all.filter((c: IClientRow): boolean => c.provider === 'entra').length}</strong></div>
        <div className="bu-card bu-stat"><span>Google Workspace</span><strong>{all.filter((c: IClientRow): boolean => c.provider === 'google').length}</strong></div>
      </div>

      {msg !== null && <div className={`bu-alert ${msg.ok ? 'bu-alert-ok' : 'bu-alert-danger'}`} style={{ marginBottom: 18 }}>{msg.text}</div>}

      <div className="bu-card" style={{ overflow: 'hidden' }}>
        {clients === null ? <div className="bu-card-pad" style={{ color: 'var(--text-3)' }}>Loading...</div> : all.length === 0 ? (
          <div className="bu-empty"><EmptyArt /><h3>No clients yet</h3><p>Add your first client organization to get started.</p></div>
        ) : (
          <div className="bu-table-wrap">
            <table className="bu-table">
              <thead><tr><th>Client</th><th>Sign-in</th><th>Apps</th><th>People</th><th>Status</th><th /></tr></thead>
              <tbody>
                {all.map((c: IClientRow) => (
                  <tr key={c.tenantId}>
                    <td>
                      <div style={{ fontWeight: 700 }}>{c.name}</div>
                      <div className="bu-mono" style={{ color: 'var(--text-3)', marginTop: 2 }}>{c.tenantId === me.tenantId ? 'Home organization' : c.tenantId}</div>
                    </td>
                    <td><ProviderTag p={c.provider} domain={c.domain} /></td>
                    <td>{c.apps.length === 0 ? <span style={{ color: 'var(--text-3)' }}>None</span> : c.apps.map((k: string) => <span key={k} className="bu-chip" style={{ marginRight: 6 }}>{(catalog.filter((a: IAppInfo): boolean => a.key === k)[0] || { name: k }).name}</span>)}</td>
                    <td style={{ color: 'var(--text-2)' }}>{c.people}<div className="bu-person-mail">{c.owners.length === 0 ? 'No owner yet' : `${c.owners.length} owner${c.owners.length === 1 ? '' : 's'}`}</div></td>
                    <td><span className={`bu-badge ${c.active ? 'bu-badge-ok' : 'bu-badge-off'}`}>{c.active ? 'Active' : 'Suspended'}</span></td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {c.provider === 'entra' && c.tenantId !== me.tenantId && (
                        <button className="bu-btn bu-btn-ghost bu-btn-sm" onClick={(): void => copy(consentUrl(c.tenantId), c.tenantId)} title="Copy the one-time Microsoft consent link for this client's admin">
                          <Icon name={copied === c.tenantId ? 'check' : 'link'} size={15} /> {copied === c.tenantId ? 'Copied' : 'Consent link'}
                        </button>
                      )}
                      <Link to={`/admin/team?tenantId=${c.tenantId}`} className="bu-btn bu-btn-ghost bu-btn-sm" style={{ marginLeft: 6 }}><Icon name="users" size={15} /> People</Link>
                      <button className="bu-btn bu-btn-secondary bu-btn-sm" style={{ marginLeft: 6 }} onClick={(): void => {
                        setFormErr('');
                        setForm({ isNew: false, provider: c.provider, tenantId: c.tenantId, domain: c.domain, lookupDomain: '', name: c.name, active: c.active, apps: c.apps, owners: [], currentOwners: c.owners });
                      }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {form !== null && (
        <div className="bu-drawer-backdrop" onClick={(): void => setForm(null)}>
          <div className="bu-drawer" onClick={(e: React.MouseEvent<HTMLDivElement>): void => e.stopPropagation()}>
            <div className="bu-drawer-head">
              <div>
                <p className="bu-eyebrow" style={{ marginBottom: 4 }}>{form.isNew ? 'New client' : 'Edit client'}</p>
                <h2 className="bu-card-title" style={{ fontSize: 20 }}>{form.isNew ? 'Add a client organization' : form.name}</h2>
              </div>
              <button className="bu-btn bu-btn-ghost bu-btn-sm" onClick={(): void => setForm(null)} aria-label="Close">&times;</button>
            </div>
            <div className="bu-drawer-body">
              {form.isNew && (
                <div className="bu-field">How do they sign in?
                  <div className="bu-seg">
                    <button className={form.provider === 'entra' ? 'on' : ''} onClick={(): void => setForm({ ...form, provider: 'entra' })}><MicrosoftMark size={15} /> Microsoft 365</button>
                    <button className={form.provider === 'google' ? 'on' : ''} onClick={(): void => setForm({ ...form, provider: 'google' })}><GoogleMark size={15} /> Google Workspace</button>
                  </div>
                </div>
              )}

              {form.provider === 'entra' ? (
                form.isNew ? (
                  <React.Fragment>
                    <label className="bu-field">Their email domain <small>We look up their Microsoft 365 organization from it.</small>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <input className="bu-input" placeholder="contoso.com" value={form.lookupDomain} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setForm({ ...form, lookupDomain: e.target.value })}
                          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>): void => { if (e.key === 'Enter') { void lookup(); } }} />
                        <button className="bu-btn bu-btn-secondary" disabled={busy || form.lookupDomain.trim() === ''} onClick={(): void => { void lookup(); }}>Look up</button>
                      </div>
                    </label>
                    <label className="bu-field">Organization ID <small>Filled in by Look up.</small>
                      <input className="bu-input bu-mono" value={form.tenantId} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setForm({ ...form, tenantId: e.target.value.trim() })} />
                    </label>
                  </React.Fragment>
                ) : <div className="bu-field">Organization ID<span className="bu-mono" style={{ color: 'var(--text-2)', fontWeight: 500 }}>{form.tenantId}</span></div>
              ) : (
                <label className="bu-field">Google Workspace domain <small>Anyone signing in with an account on this domain belongs to this client.</small>
                  <input className="bu-input" placeholder="acme.com" disabled={!form.isNew} value={form.domain} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setForm({ ...form, domain: e.target.value.trim().toLowerCase() })} />
                </label>
              )}

              <label className="bu-field">Client name
                <input className="bu-input" value={form.name} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setForm({ ...form, name: e.target.value })} />
              </label>

              <div className="bu-field">Apps
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 2 }}>
                  {catalog.map((a: IAppInfo) => (
                    <label key={a.key} className="bu-check">
                      <input type="checkbox" checked={form.apps.indexOf(a.key) !== -1} onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                        setForm({ ...form, apps: e.target.checked ? [...form.apps, a.key] : form.apps.filter((k: string): boolean => k !== a.key) })} />
                      {a.name}
                    </label>
                  ))}
                </div>
              </div>

              <div className="bu-field">{form.isNew ? 'Owners' : 'Add owners'}
                <small>
                  {form.currentOwners.length > 0 ? `Current: ${form.currentOwners.join(', ')}. ` : 'No owner yet. '}
                  Owners manage everyone else on the client's People page. Use people from the organization itself{form.provider === 'google' && form.domain ? ` (@${form.domain})` : ''}; invite anyone else from People.
                </small>
                <EmailList value={form.owners} onChange={(v: string[]): void => setForm({ ...form, owners: v })} domain={form.provider === 'google' ? form.domain || undefined : undefined} />
              </div>

              {!form.isNew && (
                <label className="bu-check"><input type="checkbox" checked={form.active} onChange={(e: React.ChangeEvent<HTMLInputElement>): void => setForm({ ...form, active: e.target.checked })} /> Active <span style={{ color: 'var(--text-3)', fontWeight: 500 }}>(suspend to block sign-in; data is kept)</span></label>
              )}

              {form.isNew && form.provider === 'entra' && (
                <div className="bu-alert bu-alert-info"><Icon name="link" size={18} /><span>After saving, use <strong>Consent link</strong> and send it to their Microsoft 365 admin. They approve BeefedUp Apps once for their organization.</span></div>
              )}
              {formErr !== '' && <div className="bu-alert bu-alert-danger">{formErr}</div>}
            </div>
            <div className="bu-drawer-foot">
              <button className="bu-btn bu-btn-ghost" onClick={(): void => setForm(null)}>Cancel</button>
              <button className="bu-btn bu-btn-primary" disabled={busy || !canSave} onClick={(): void => { void save(); }}>{busy ? 'Saving...' : form.isNew ? 'Add client' : 'Save changes'}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};
